import React, { useState, useEffect } from "react";
import { 
  Search, Trash2, CheckCircle, Clock, User, Phone, 
  Mail, FileText, X, LogOut, Filter, ShieldAlert, ChevronRight, Check
} from "lucide-react";

export const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    if (token) {
      fetchLeads();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Invalid login credentials");
      }

      const data = await response.json();
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      setUsername("");
      setPassword("");
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setLeads([]);
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    setApiError("");
    try {
      const response = await fetch(`${backendUrl}/api/admin/leads`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error("Session expired. Please login again.");
        }
        throw new Error("Failed to fetch leads");
      }

      const data = await response.json();
      setLeads(data);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLeadsLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/leads/${leadId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error("Failed to update lead status");
      }

      // Update locally
      setLeads(leads.map(lead => lead.id === leadId ? { ...lead, status: newStatus } : lead));
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to permanently delete this lead?")) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/leads/${leadId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete lead");
      }

      // Update locally
      setLeads(leads.filter(lead => lead.id !== leadId));
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(null);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Filter and search leads
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone.includes(searchQuery) ||
      (lead.message && lead.message.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (lead.service && lead.service.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = 
      statusFilter === "All" || 
      (statusFilter === "New" && (lead.status === "New" || !lead.status)) ||
      lead.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-emerald-100 text-[#064e3b] border-emerald-200";
      case "Contacted":
        return "bg-blue-100 text-[#1e3a8a] border-blue-200";
      case "New":
      default:
        return "bg-[#fef9c3] text-[#713f12] border-[#fef08a]";
    }
  };

  // Count leads for analytics cards
  const totalLeadsCount = leads.length;
  const newLeadsCount = leads.filter(l => !l.status || l.status === "New").length;
  const contactedLeadsCount = leads.filter(l => l.status === "Contacted").length;
  const completedLeadsCount = leads.filter(l => l.status === "Completed").length;

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAF8] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <span className="font-['Outfit'] text-2xl font-black text-[#1A2E20] tracking-tight">
              IMPERIAL <span className="text-[#2E5C3E]">LANDSCAPING</span>
            </span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-black tracking-tight text-[#1A2E20] font-['Outfit']">
            Admin Portal Login
          </h2>
          <p className="mt-2 text-center text-sm text-[#4A6E55]">
            Authorized access only for TLH landscaping operations
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-[0_20px_50px_-20px_rgba(26,46,32,0.15)] rounded-3xl border border-stone-100 sm:px-10">
            <form className="space-y-6" onSubmit={handleLogin}>
              {loginError && (
                <div className="rounded-xl bg-red-50 p-4 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-red-500 shrink-0" />
                  <span>{loginError}</span>
                </div>
              )}

              <div>
                <label htmlFor="username" className="block text-sm font-semibold text-[#1A2E20]">
                  Username
                </label>
                <div className="mt-1">
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-stone-200 rounded-xl shadow-sm placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#2E5C3E] focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-[#1A2E20]">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-stone-200 rounded-xl shadow-sm placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#2E5C3E] focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-[#2E5C3E] hover:bg-[#1A2E20] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2E5C3E] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Signing in..." : "Access Dashboard"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F6F3] text-[#1A2E20]">
      {/* Navbar */}
      <nav className="bg-[#1A2E20] text-white py-4 px-6 sticky top-0 z-30 shadow-md">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="font-['Outfit'] text-lg font-black tracking-wider uppercase text-[#EAB308]">
              Imperial Landscaping, TLH
            </span>
            <span className="bg-emerald-800 text-emerald-100 text-xs px-2.5 py-0.5 rounded-full font-bold">
              Leads Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchLeads}
              className="text-white/80 hover:text-white text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/10 transition"
            >
              Refresh Leads
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs font-bold text-red-200 hover:text-white bg-red-900/40 hover:bg-red-900/80 px-3.5 py-1.5 rounded-lg transition"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm flex flex-col justify-between">
            <span className="text-xs font-bold tracking-wider uppercase text-[#4A6E55]">Total Leads</span>
            <span className="font-['Outfit'] text-3xl font-black text-[#1A2E20] mt-2">
              {leadsLoading ? "..." : totalLeadsCount}
            </span>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm flex flex-col justify-between">
            <span className="text-xs font-bold tracking-wider uppercase text-[#713f12]">New Leads</span>
            <span className="font-['Outfit'] text-3xl font-black text-[#EAB308] mt-2">
              {leadsLoading ? "..." : newLeadsCount}
            </span>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm flex flex-col justify-between">
            <span className="text-xs font-bold tracking-wider uppercase text-[#1e3a8a]">Contacted</span>
            <span className="font-['Outfit'] text-3xl font-black text-blue-600 mt-2">
              {leadsLoading ? "..." : contactedLeadsCount}
            </span>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm flex flex-col justify-between">
            <span className="text-xs font-bold tracking-wider uppercase text-[#064e3b]">Completed</span>
            <span className="font-['Outfit'] text-3xl font-black text-emerald-600 mt-2">
              {leadsLoading ? "..." : completedLeadsCount}
            </span>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-6">
          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4A6E55] w-4.5 h-4.5" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2E5C3E] text-sm"
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="flex flex-wrap gap-1.5 p-1 bg-stone-200/60 rounded-xl w-full md:w-auto">
            {["All", "New", "Contacted", "Completed"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`flex-1 md:flex-none px-4 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${
                  statusFilter === status 
                    ? "bg-white text-[#1A2E20] shadow-sm" 
                    : "text-[#4A6E55] hover:text-[#1A2E20]"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Content Layout */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Leads Table/List */}
          <div className={`${selectedLead ? "lg:col-span-7" : "lg:col-span-12"} bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden`}>
            {leadsLoading ? (
              <div className="p-12 text-center text-sm text-[#4A6E55] font-medium">
                Loading lead inquiries...
              </div>
            ) : apiError ? (
              <div className="p-12 text-center text-sm text-red-600 font-semibold bg-red-50/50">
                Error: {apiError}
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="p-12 text-center text-sm text-[#4A6E55] font-medium">
                No matching inquiries found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-stone-100 text-left text-sm">
                  <thead className="bg-[#F8FAF8] text-[#1A2E20] font-bold text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4">Client</th>
                      <th className="px-6 py-4">Service</th>
                      <th className="px-6 py-4 hidden md:table-cell">Submitted</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 bg-white">
                    {filteredLeads.map((lead) => (
                      <tr 
                        key={lead.id} 
                        onClick={() => setSelectedLead(lead)}
                        className={`hover:bg-[#F3F6F3]/40 cursor-pointer transition ${
                          selectedLead?.id === lead.id ? "bg-[#EEF2EF]" : ""
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="font-bold text-[#1A2E20]">{lead.name}</div>
                          <div className="text-xs text-[#4A6E55]">{lead.phone}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-xs text-[#2E5C3E] px-2.5 py-1 rounded-md bg-[#EEF2EF] inline-block max-w-[150px] truncate">
                            {lead.service}
                          </span>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell text-xs text-[#4A6E55]">
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric"
                          }) : "N/A"}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(lead.status || "New")}`}>
                            {lead.status || "New"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => deleteLead(lead.id)}
                              className="p-2 text-stone-400 hover:text-red-600 rounded-lg hover:bg-stone-50 transition"
                              title="Delete inquiry"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="p-2 text-stone-400 hover:text-[#2E5C3E] rounded-lg hover:bg-stone-50 transition"
                              title="View details"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Lead Details Pane */}
          {selectedLead && (
            <div className="lg:col-span-5 bg-white rounded-3xl border border-stone-100 shadow-lg overflow-hidden sticky top-24">
              <div className="bg-[#1A2E20] text-white p-5 flex justify-between items-center">
                <div>
                  <h3 className="font-['Outfit'] font-black text-lg">Inquiry Details</h3>
                  <p className="text-xs text-stone-300">
                    ID: {selectedLead.id.substring(0, 8)}...
                  </p>
                </div>
                <button 
                  onClick={() => setSelectedLead(null)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Card */}
                <div className="space-y-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#EEF2EF] text-[#2E5C3E] grid place-items-center">
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-[#4A6E55] font-semibold">Client Name</div>
                      <div className="text-sm font-bold text-[#1A2E20]">{selectedLead.name}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#EEF2EF] text-[#2E5C3E] grid place-items-center">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-[#4A6E55] font-semibold">Phone Number</div>
                      <a href={`tel:${selectedLead.phone}`} className="text-sm font-bold text-[#2E5C3E] hover:underline">
                        {selectedLead.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#EEF2EF] text-[#2E5C3E] grid place-items-center">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-[#4A6E55] font-semibold">Email Address</div>
                      <a href={`mailto:${selectedLead.email}`} className="text-sm font-bold text-[#2E5C3E] hover:underline">
                        {selectedLead.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#EEF2EF] text-[#2E5C3E] grid place-items-center">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-[#4A6E55] font-semibold">Requested Service</div>
                      <div className="text-sm font-bold text-[#1A2E20]">{selectedLead.service}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#EEF2EF] text-[#2E5C3E] grid place-items-center">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-[#4A6E55] font-semibold">Submitted At</div>
                      <div className="text-sm font-bold text-[#1A2E20]">
                        {selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleString() : "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Message block */}
                <div>
                  <div className="text-xs text-[#4A6E55] font-bold uppercase tracking-wider mb-2">Message Inquired</div>
                  <div className="bg-stone-50 border border-stone-100 rounded-2xl p-4 text-sm text-[#1A2E20] leading-relaxed whitespace-pre-line max-h-48 overflow-y-auto font-sans">
                    &quot;{selectedLead.message}&quot;
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Status Updater */}
                <div>
                  <div className="text-xs text-[#4A6E55] font-bold uppercase tracking-wider mb-3">Update Status</div>
                  <div className="grid grid-cols-3 gap-2">
                    {["New", "Contacted", "Completed"].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateLeadStatus(selectedLead.id, status)}
                        className={`py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 border border-stone-200 ${
                          (selectedLead.status || "New") === status
                            ? "bg-[#2E5C3E] text-white border-[#2E5C3E]"
                            : "bg-white text-stone-600 hover:bg-[#EEF2EF] hover:text-[#1A2E20]"
                        }`}
                      >
                        {(selectedLead.status || "New") === status && <Check className="w-3.5 h-3.5 shrink-0" />}
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Delete button inside detail */}
                <div className="pt-2">
                  <button
                    onClick={() => deleteLead(selectedLead.id)}
                    className="w-full py-3 border-2 border-red-100 hover:border-red-200 bg-red-50 text-red-700 hover:text-red-900 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4 shrink-0" /> Permanent Delete Inquiry
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};